import { FunctionComponent, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import modalStyle from "./styles/Modal.module.css";
import modalBottomStyle from "./styles/ModalBottom.module.css";
import { LabelPairedXmarkMdBoldIcon } from "@deriv/quill-icons";
import { useDevice } from "../../../hooks/useDevice";
import { useDragToClose } from "../../../hooks/useDragToClose";
import useScrollDisable from "../../../hooks/useScrollDisable";
import BasicButton, { BasicButtonType } from "../../buttons/basic";

const getStyleForType = (type: string) => {
  return type === "bottom" ? modalBottomStyle : modalStyle;
};

export type BaseModalType = {
  className?: string;
  description?: string;
  title?: string;

  /** Variant props */
  height?: "hug-content" | "fill-container";
  type?: "auto" | "bottom" | "overlay";

  /* Custom props */
  buttonPrimary?: BasicButtonType;
  buttonSecondary?: BasicButtonType;
  show: boolean;
  onClose?: () => void;
  customIcon?: ReactNode;
  customImage?: string;

  /** Display props */
  showCloseButton?: boolean;
  showHandleBar?: boolean;

  /** Content prop */
  content?: ReactNode;
};

const ModalBase: FunctionComponent<BaseModalType> = ({
  className = "",
  height = "hug-content",
  description = "Add the content here",
  title = "Title",
  buttonPrimary,
  buttonSecondary,
  show = false,
  type: defaultType = "auto",
  onClose,
  showCloseButton = true,
  showHandleBar = true,
  customIcon,
  customImage,
  content,
}) => {
  const [type, setType] = useState(defaultType);
  const styles = getStyleForType(type);
  const modalId = `quill-modal-${type}`;

  const { isDesktop, isDeviceDetected } = useDevice();

  // Use the drag to close hook
  const { dragHandlers, dragStyles } = useDragToClose({
    isEnabled: type === "bottom",
    onClose,
    threshold: 150,
  });

  // Disable body scrolling when the modal is shown
  useScrollDisable(show);

  // Update type based on device and defaultType
  useEffect(() => {
    if (isDeviceDetected) {
      if (defaultType === "auto") {
        setType(isDesktop ? "overlay" : "bottom");
      } else if (defaultType !== type) {
        setType(defaultType);
      }
    }
  }, [isDesktop, isDeviceDetected, defaultType]);

  const modalContent = (
    <div
      className={[
        styles.root,
        className,
        "quill-ui-next-component",
        type === "bottom" ? "quill-ui-next-actionsheet-full-width" : "",
      ].join(" ")}
      data-height={height}
      data-type={type}
      data-id={modalId}
      style={dragStyles}
    >
      {customImage && (
        <img className={styles.containerImage} alt="" src={customImage} />
      )}
      {customIcon && (
        <div className={styles.containerIcon}>
          <div className={styles.customIcon}>
            <div className={styles.icon}>{customIcon}</div>
          </div>
        </div>
      )}
      <div className={styles.titledescriptioncontent}>
        <div className={styles.title}>{title}</div>
        {description && <div className={styles.description}>{description}</div>}

        {content &&
          (type === "bottom" ? (
            <div
              className={`${styles.containerContent} quill-modal-bottom-container`}
            >
              <div className="quill-ui-next-modal-bottom-content">
                {content}
              </div>
            </div>
          ) : (
            <div
              className={`${styles.containerContent} quill-ui-next-modal-content`}
            >
              {content}
            </div>
          ))}
      </div>
      <div
        className={
          type === "bottom" ? styles.containerAction : styles.containerButtons
        }
      >
        <div className={styles.actionButtons}>
          {buttonPrimary && (
            <BasicButton
              color="blackWhite"
              {...buttonPrimary}
              style="primary"
              width="fill container"
            />
          )}
          {buttonSecondary && (
            <BasicButton
              color="blackWhite"
              {...buttonSecondary}
              style="secondary"
              width="fill container"
            />
          )}
        </div>
      </div>

      {showCloseButton && type === "overlay" && (
        <div className={styles.containerButtonClose}>
          <div className={styles.buttonClose}>
            <div className={styles.iconCustom}>
              <div
                className={`${styles.icon7} quill-ui-next-pointer`}
                onClick={onClose}
              >
                <LabelPairedXmarkMdBoldIcon />
              </div>
            </div>
            <div className={styles.bgFocus2} />
            <div className={styles.iconColorReference}>Label</div>
          </div>
        </div>
      )}

      {showHandleBar && type === "bottom" && (
        <div className={styles.containerHandleBar} {...dragHandlers}>
          <div className={styles.handleBar} />
        </div>
      )}
    </div>
  );

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  if (!show) {
    return null;
  }

  return createPortal(
    <div
      className={`quill-ui-next-overlay type-${type}`}
      onClick={handleOverlayClick}
    >
      {modalContent}
    </div>,
    document.body
  );
};

export default ModalBase;
